import { useState } from 'react';
import { useT } from '../i18n/translations';
import './MeetingVote.css';

/* ── Icons ─────────────────────────────────────────────────────────────── */
const IconVote = () => (
  <svg viewBox="0 0 24 24"><path d="M18 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm-5 14H7v-2h6v2zm4-4H7v-2h10v2zm0-4H7V7h10v2z"/></svg>
);
const IconClose = () => (
  <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
);
const IconPlus = () => (
  <svg viewBox="0 0 24 24"><path d="M19 13H13v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24"><path d="M16 9v10H8V9h8m-1.5-6h-5l-1 1H5v2h14V4h-3.5l-1-1zM18 7H6v12c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7z"/></svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"/></svg>
);
const IconEye = () => (
  <svg viewBox="0 0 24 24"><path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8a3 3 0 1 0 0 6 3 3 0 0 0 0-6z"/></svg>
);
const IconEyeOff = () => (
  <svg viewBox="0 0 24 24"><path d="M12 7c2.76 0 5 2.24 5 5 0 .65-.13 1.26-.36 1.83l2.92 2.92c1.51-1.26 2.7-2.89 3.43-4.75-1.73-4.39-6-7.5-11-7.5-1.4 0-2.74.25-3.98.7l2.16 2.16C10.74 7.13 11.35 7 12 7zM2 4.27l2.28 2.28.46.46A11.804 11.804 0 0 0 1 12c1.73 4.39 6 7.5 11 7.5 1.55 0 3.03-.3 4.38-.84l.42.42L19.73 22 21 20.73 3.27 3 2 4.27zM7.53 9.8l1.55 1.55c-.05.21-.08.43-.08.65 0 1.66 1.34 3 3 3 .22 0 .44-.03.65-.08l1.55 1.55c-.67.33-1.41.53-2.2.53-2.76 0-5-2.24-5-5 0-.79.2-1.53.53-2.2zm4.31-.78 3.15 3.15.02-.16c0-1.66-1.34-3-3-3l-.17.01z"/></svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24"><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zM12 17c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm3.1-9H8.9V6c0-1.71 1.39-3.1 3.1-3.1 1.71 0 3.1 1.39 3.1 3.1v2z"/></svg>
);

/* ── Palette for up to 6 options ───────────────────────────────────────── */
const SLICE_COLORS = ['#02c3a6', '#005f7a', '#f59e0b', '#e53935', '#7c3aed', '#0ea5e9'];

/* ════════════════════════════════════════════════════════════════════════
   DonutChart  — SVG pie chart showing vote proportions
   Props: tally number[], options string[], total number, pickedIdx number|null
════════════════════════════════════════════════════════════════════════ */
function DonutChart({ tally, options, total, pickedIdx }) {
  const SIZE = 130;
  const R    = 60;
  const CX   = SIZE / 2;
  const CY   = SIZE / 2;

  let cumAngle = -90; // start at top
  const slices = tally.map((count, i) => {
    const pct   = total > 0 ? count / total : 0;
    const angle = pct * 360;
    const start = cumAngle;
    cumAngle   += angle;

    const startRad = (start * Math.PI) / 180;
    const endRad   = ((start + angle) * Math.PI) / 180;
    const x1 = CX + R * Math.cos(startRad);
    const y1 = CY + R * Math.sin(startRad);
    const x2 = CX + R * Math.cos(endRad);
    const y2 = CY + R * Math.sin(endRad);
    const large = angle > 180 ? 1 : 0;

    // Solid pie slice: arc → center → back
    const d = pct === 0 ? '' : pct === 1
      ? `M ${CX} ${CY - R} A ${R} ${R} 0 1 1 ${CX - 0.01} ${CY - R} Z`
      : `M ${CX} ${CY} L ${x1} ${y1} A ${R} ${R} 0 ${large} 1 ${x2} ${y2} Z`;

    // Label position at mid-angle
    const midRad = ((start + angle / 2) * Math.PI) / 180;
    const lx = CX + R * 0.65 * Math.cos(midRad);
    const ly = CY + R * 0.65 * Math.sin(midRad);
    const pct100 = Math.round(pct * 100);

    return { d, color: SLICE_COLORS[i % SLICE_COLORS.length], pct, pct100, count, i, lx, ly };
  });

  if (total === 0) {
    return (
      <div className="mvote-donut">
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle cx={CX} cy={CY} r={R} fill="#e8edf2" />
        </svg>
        <div className="mvote-donut__legend">
          {options.map((opt, i) => (
            <div key={i} className="mvote-donut__legend-row">
              <span className="mvote-donut__legend-dot" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }} />
              <span className="mvote-donut__legend-label">{opt}</span>
              <span className="mvote-donut__legend-pct">0%</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mvote-donut">
      <div className="mvote-donut__chart-wrap">
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`} className="mvote-donut__svg">
          {slices.map(s => s.d && (
            <path
              key={s.i}
              d={s.d}
              fill={s.color}
              stroke="#fff"
              strokeWidth="2"
              className="mvote-donut__slice"
              style={{
                opacity: pickedIdx == null || pickedIdx === s.i ? 1 : 0.7,
                transform: pickedIdx === s.i ? 'scale(1.04)' : 'scale(1)',
                transformOrigin: `${CX}px ${CY}px`,
                transition: 'transform 0.2s, opacity 0.2s',
              }}
            />
          ))}
          {/* Percentage labels on slices > 8% */}
          {slices.map(s => s.pct100 > 8 && (
            <text
              key={`lbl-${s.i}`}
              x={s.lx}
              y={s.ly}
              textAnchor="middle"
              dominantBaseline="middle"
              fontSize="10"
              fontWeight="800"
              fill="#fff"
              style={{ pointerEvents: 'none', fontFamily: 'Cairo, sans-serif' }}
            >
              {s.pct100}%
            </text>
          ))}
        </svg>
      </div>

      {/* Legend */}
      <div className="mvote-donut__legend">
        {options.map((opt, i) => {
          const pct   = total > 0 ? Math.round((tally[i] / total) * 100) : 0;
          const count = tally[i];
          return (
            <div key={i} className={`mvote-donut__legend-row${pickedIdx === i ? ' mvote-donut__legend-row--picked' : ''}`}>
              <span className="mvote-donut__legend-dot" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }} />
              <span className="mvote-donut__legend-label">{opt}</span>
              <span className="mvote-donut__legend-count">{count}</span>
              <span className="mvote-donut__legend-pct" style={{ color: SLICE_COLORS[i % SLICE_COLORS.length] }}>{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   CreateVoteModal  — host fills title + options then launches
   Props: onLaunch(title, options[])  onClose()
════════════════════════════════════════════════════════════════════════ */
export function CreateVoteModal({ onLaunch, onClose }) {
  const t = useT();
  const [title,   setTitle]   = useState('');
  const [options, setOptions] = useState(['', '']);
  const [error,   setError]   = useState('');

  function setOption(i, val) {
    setOptions(prev => prev.map((o, j) => j === i ? val : o));
  }

  function addOption() {
    if (options.length < 6) setOptions(prev => [...prev, '']);
  }

  function removeOption(i) {
    if (options.length <= 2) return;
    setOptions(prev => prev.filter((_, j) => j !== i));
  }

  function handleLaunch() {
    const filled = options.map(o => o.trim()).filter(Boolean);
    if (!title.trim()) { setError(t('vote.title')); return; }
    if (filled.length < 2) { setError(t('vote.minOptions')); return; }
    onLaunch(title.trim(), filled);
  }

  return (
    <div className="mvote-overlay" onClick={onClose}>
      <div className="mvote-modal" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="mvote-modal__header">
          <div className="mvote-modal__header-left">
            <div className="mvote-modal__header-icon"><IconVote /></div>
            <span>{t('vote.newVote')}</span>
          </div>
          <button className="mvote-modal__close" onClick={onClose}><IconClose /></button>
        </div>

        {/* Body */}
        <div className="mvote-modal__body">
          {/* Title */}
          <label className="mvote-modal__label">{t('vote.title')}</label>
          <input
            className="mvote-modal__input"
            placeholder={t('vote.titlePh')}
            value={title}
            onChange={e => { setTitle(e.target.value); setError(''); }}
          />

          {/* Options */}
          <label className="mvote-modal__label" style={{ marginTop: 16 }}>{t('vote.options')}</label>
          <div className="mvote-modal__options">
            {options.map((opt, i) => (
              <div key={i} className="mvote-modal__option-row">
                <div className="mvote-modal__option-num">{i + 1}</div>
                <input
                  className="mvote-modal__input mvote-modal__input--opt"
                  value={opt}
                  onChange={e => { setOption(i, e.target.value); setError(''); }}
                  placeholder={`${t('vote.options')} ${i + 1}`}
                />
                {options.length > 2 && (
                  <button className="mvote-modal__del-btn" onClick={() => removeOption(i)}>
                    <IconTrash />
                  </button>
                )}
              </div>
            ))}
            {options.length < 6 && (
              <button className="mvote-modal__add-btn" onClick={addOption}>
                <IconPlus /> {t('vote.addOption')}
              </button>
            )}
          </div>

          {error && <p className="mvote-modal__error">{error}</p>}
        </div>

        {/* Footer */}
        <div className="mvote-modal__footer">
          <button className="mvote-modal__launch-btn" onClick={handleLaunch}>
            <IconVote /> {t('vote.launch')}
          </button>
        </div>

      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   VotePopup  — shown to all participants during a live vote
   Props:
     vote        { voteId, title, options, creatorId, creatorName,
                   showResults, closed, totalVotes, tally }
     myUserId    string
     myVote      number|null   — optionIndex the local user picked (or null)
     isCreator   boolean
     onCast      fn(optionIndex)
     onToggleResults fn(show)
     onClose     fn()
     onDismiss   fn()          — creator removes popup for everyone
     onCloseVote fn()          — creator closes voting
════════════════════════════════════════════════════════════════════════ */
export function VotePopup({
  vote,
  myUserId,
  myVote,
  isCreator,
  onCast,
  onToggleResults,
  onClose,
  onDismiss,
  onCloseVote,
}) {
  const t = useT();
  const [localVote, setLocalVote] = useState(myVote ?? null);
  const [casting,   setCasting]   = useState(false);

  async function handleCast(idx) {
    if (vote.closed || casting) return;
    setLocalVote(idx);
    setCasting(true);
    try { await onCast(idx); } finally { setCasting(false); }
  }

  return (
    <div className="mvote-popup-wrap">
      <div className="mvote-popup">

        {/* Header */}
        <div className="mvote-popup__header">
          <div className="mvote-popup__header-left">
            <div className="mvote-popup__icon"><IconVote /></div>
            <div>
              <p className="mvote-popup__by">{t('vote.byLabel')} {vote.creatorName}</p>
              <h3 className="mvote-popup__title">{vote.title}</h3>
            </div>
          </div>
          <button className="mvote-popup__close" onClick={onClose} title={t('common.close')}>
            <IconClose />
          </button>
        </div>

        {/* Status bar */}
        <div className="mvote-popup__status-bar">
          {vote.closed
            ? <span className="mvote-popup__chip mvote-popup__chip--closed"><IconLock />{t('vote.closed')}</span>
            : <span className="mvote-popup__chip mvote-popup__chip--live"><span className="mvote-popup__pulse" />{t('room.live')}</span>
          }
          <span className="mvote-popup__total">{vote.totalVotes} {t('vote.totalVotes')}</span>
        </div>

        {/* Options — vote buttons */}
        <div className="mvote-popup__options">
          {vote.options.map((opt, i) => {
            const picked = localVote === i;
            return (
              <button
                key={i}
                className={`mvote-popup__option${picked ? ' mvote-popup__option--picked' : ''}${vote.closed ? ' mvote-popup__option--closed' : ''}`}
                style={picked ? { borderColor: SLICE_COLORS[i % SLICE_COLORS.length], background: `${SLICE_COLORS[i % SLICE_COLORS.length]}18` } : {}}
                onClick={() => handleCast(i)}
                disabled={vote.closed || casting}
              >
                <div className="mvote-popup__opt-top">
                  <span className="mvote-popup__opt-dot" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }} />
                  <span className="mvote-popup__opt-label">{opt}</span>
                  {picked && <span className="mvote-popup__check" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }}><IconCheck /></span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* Donut chart results */}
        {vote.showResults && vote.tally && (
          <DonutChart
            tally={vote.tally}
            options={vote.options}
            total={vote.totalVotes}
            pickedIdx={localVote}
          />
        )}

        {/* Results hidden notice */}
        {!vote.showResults && (
          <p className="mvote-popup__hidden-note">
            <IconEyeOff /> {t('vote.resultsHidden')}
          </p>
        )}

        {/* Creator controls */}
        {isCreator && (
          <div className="mvote-popup__creator-bar">
            <button
              className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--eye"
              onClick={() => onToggleResults(!vote.showResults)}
            >
              {vote.showResults ? <><IconEyeOff />{t('vote.hideResults')}</> : <><IconEye />{t('vote.showResults')}</>}
            </button>

            {!vote.closed && (
              <button
                className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--lock"
                onClick={onCloseVote}
              >
                <IconLock /> {t('vote.closeVote')}
              </button>
            )}

            <button
              className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--dismiss"
              onClick={onDismiss}
            >
              <IconClose /> {t('vote.dismiss')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════════════════
   DecisionsList  — slide-in panel listing all votes with detail drill-down
════════════════════════════════════════════════════════════════════════ */
export function DecisionsList({
  votes,
  myVotes,
  detailVoteId,
  onSelectVote,
  onClose,
  onCast,
  onToggleResults,
  onCloseVote,
  onDismiss,
  isHost,
}) {
  const t = useT();
  const [casting, setCasting] = useState(false);

  const detailVote = votes.find(v => v.voteId === detailVoteId) ?? null;

  async function handleCast(voteId, idx) {
    setCasting(true);
    try { await onCast(voteId, idx); } finally { setCasting(false); }
  }

  return (
    <div className="mdlist">
      {/* Header */}
      <div className="mdlist__header">
        <div className="mdlist__header-left">
          {detailVote ? (
            <button className="mdlist__back-btn" onClick={() => onSelectVote(null)}>
              <svg viewBox="0 0 24 24"><path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/></svg>
            </button>
          ) : (
            <div className="mdlist__header-icon"><IconVote /></div>
          )}
          <span>{detailVote ? detailVote.title : t('vote.decisionsList')}</span>
          {!detailVote && votes.length > 0 && (
            <span className="mdlist__count">{votes.length}</span>
          )}
        </div>
        <button className="mdlist__close-btn" onClick={onClose}><IconClose /></button>
      </div>

      {/* Body */}
      <div className="mdlist__body">
        {/* ── LIST VIEW ── */}
        {!detailVote && (
          <>
            {votes.length === 0 && (
              <div className="mdlist__empty">
                <IconVote />
                <p>{t('vote.noDecisions')}</p>
              </div>
            )}
            {votes.map(vote => {
              const myIdx   = myVotes.get(vote.voteId);
              const myLabel = myIdx != null ? vote.options[myIdx] : null;
              return (
                <button
                  key={vote.voteId}
                  className="mdlist__item"
                  onClick={() => onSelectVote(vote.voteId)}
                >
                  <div className="mdlist__item-left">
                    <div className={`mdlist__item-dot${vote.closed ? ' mdlist__item-dot--closed' : ' mdlist__item-dot--live'}`} />
                    <div className="mdlist__item-info">
                      <span className="mdlist__item-title">{vote.title}</span>
                      <span className="mdlist__item-meta">
                        {vote.closed ? t('vote.closed') : t('room.live')}
                        {' · '}{vote.totalVotes} {t('vote.totalVotes')}
                        {myLabel ? ` · ✓ ${myLabel}` : ''}
                      </span>
                    </div>
                  </div>
                  <svg className="mdlist__chevron" viewBox="0 0 24 24"><path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/></svg>
                </button>
              );
            })}
          </>
        )}

        {/* ── DETAIL VIEW ── */}
        {detailVote && (() => {
          const vote   = detailVote;
          const myIdx  = myVotes.get(vote.voteId) ?? null;
          return (
            <div className="mdlist__detail">
              <div className="mdlist__detail-status">
                {vote.closed
                  ? <span className="mvote-popup__chip mvote-popup__chip--closed"><IconLock />{t('vote.closed')}</span>
                  : <span className="mvote-popup__chip mvote-popup__chip--live"><span className="mvote-popup__pulse" />{t('room.live')}</span>
                }
                <span className="mvote-popup__total">{vote.totalVotes} {t('vote.totalVotes')}</span>
              </div>

              <p className="mdlist__by">{t('vote.byLabel')} <strong>{vote.creatorName}</strong></p>

              {/* Vote option buttons */}
              <div className="mvote-popup__options">
                {vote.options.map((opt, i) => {
                  const picked = myIdx === i;
                  return (
                    <button
                      key={i}
                      className={`mvote-popup__option${picked ? ' mvote-popup__option--picked' : ''}${vote.closed ? ' mvote-popup__option--closed' : ''}`}
                      style={picked ? { borderColor: SLICE_COLORS[i % SLICE_COLORS.length], background: `${SLICE_COLORS[i % SLICE_COLORS.length]}18` } : {}}
                      onClick={() => !vote.closed && !casting && handleCast(vote.voteId, i)}
                      disabled={vote.closed || casting}
                    >
                      <div className="mvote-popup__opt-top">
                        <span className="mvote-popup__opt-dot" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }} />
                        <span className="mvote-popup__opt-label">{opt}</span>
                        {picked && <span className="mvote-popup__check" style={{ background: SLICE_COLORS[i % SLICE_COLORS.length] }}><IconCheck /></span>}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Donut chart results */}
              {vote.showResults && vote.tally && (
                <DonutChart
                  tally={vote.tally}
                  options={vote.options}
                  total={vote.totalVotes}
                  pickedIdx={myIdx}
                />
              )}

              {!vote.showResults && (
                <p className="mvote-popup__hidden-note"><IconEyeOff /> {t('vote.resultsHidden')}</p>
              )}

              {isHost && (
                <div className="mvote-popup__creator-bar">
                  <button className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--eye"
                    onClick={() => onToggleResults(vote.voteId, !vote.showResults)}>
                    {vote.showResults ? <><IconEyeOff />{t('vote.hideResults')}</> : <><IconEye />{t('vote.showResults')}</>}
                  </button>
                  {!vote.closed && (
                    <button className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--lock"
                      onClick={() => onCloseVote(vote.voteId)}>
                      <IconLock /> {t('vote.closeVote')}
                    </button>
                  )}
                  <button className="mvote-popup__ctrl-btn mvote-popup__ctrl-btn--dismiss"
                    onClick={() => { onDismiss(vote.voteId); onSelectVote(null); }}>
                    <IconClose /> {t('vote.dismiss')}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
